/**
 * Page de sélection des relevés d'examens pour la saisie rapide.
 * Liste les relevés scannés/traités prêts pour la saisie manuelle.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetRelevesCorrectionQuery } from '../../services/ocrApi';
import {
  Zap,
  FileText,
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertCircle,
} from 'lucide-react';

const SaisieRapideExamens: React.FC = () => {
  const navigate = useNavigate();
  const { data: releves, isLoading, error } = useGetRelevesCorrectionQuery();

  // Filtrer les relevés qui sont prêts pour la saisie (scannés ou traités)
  const relevesPretsSaisie = releves?.filter(r =>
    ['scanne', 'traite'].includes(r.status)
  ) || [];

  const relevesValides = releves?.filter(r =>
    ['verifie', 'valide'].includes(r.status)
  ) || [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'scanne':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full">
            <Clock size={12} /> Scanné
          </span>
        );
      case 'traite':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
            <AlertCircle size={12} /> Traité OCR
          </span>
        );
      case 'verifie':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
            <CheckCircle2 size={12} /> Vérifié
          </span>
        );
      case 'valide':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded-full">
            <CheckCircle2 size={12} /> Validé
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <Zap size={24} className="text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Saisie Rapide - Examens</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Sélectionnez un relevé d'examen pour commencer la saisie rapide
              </p>
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600"></div>
          <span className="ml-3 text-gray-500 dark:text-gray-400">Chargement des relevés...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
          Erreur lors du chargement des relevés.
        </div>
      )}

      {!isLoading && !error && (
        <>
          {/* Relevés prêts pour saisie */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
              <Zap size={18} className="text-orange-500" />
              Relevés prêts pour la saisie ({relevesPretsSaisie.length})
            </h2>

            {relevesPretsSaisie.length === 0 ? (
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
                <FileText size={40} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Aucun relevé d'examen en attente de saisie.</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Scannez d'abord vos relevés dans la page "OCR Manuscrit V2".
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {relevesPretsSaisie.map((releve) => (
                  <div
                    key={releve.id}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-700 transition cursor-pointer group"
                    onClick={() => navigate(`/ocr/saisie-rapide-v2/${releve.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-gray-100 group-hover:text-orange-600 transition">
                          {typeof releve.matiere === 'object' ? releve.matiere.nom_matiere : releve.matiere_nom}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {typeof releve.matiere === 'object' ? releve.matiere.code_matiere : releve.matiere_code}
                        </p>
                      </div>
                      {getStatusBadge(releve.status)}
                    </div>

                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <p>Niveau: <span className="font-medium">{releve.niveau}</span> - {releve.option}</p>
                      <p>Session: <span className="font-medium">{releve.type_examen === 'normal' ? 'Ordinaire' : 'Rattrapage'}</span></p>
                      <p>Copies: <span className="font-medium">{releve.nombre_copies}</span></p>
                      <p>Semestre: <span className="font-medium">{releve.semestre}</span> | {releve.annee_academique}</p>
                    </div>

                    <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition font-medium">
                      <Zap size={18} />
                      Commencer la saisie
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Relevés déjà traités */}
          {relevesValides.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                <CheckCircle2 size={18} className="text-green-500" />
                Relevés déjà validés ({relevesValides.length})
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {relevesValides.map((releve) => (
                  <div
                    key={releve.id}
                    className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 opacity-80"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-gray-700 dark:text-gray-300">
                          {typeof releve.matiere === 'object' ? releve.matiere.nom_matiere : releve.matiere_nom}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {typeof releve.matiere === 'object' ? releve.matiere.code_matiere : releve.matiere_code}
                        </p>
                      </div>
                      {getStatusBadge(releve.status)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      <p>{releve.niveau} - {releve.option} | {releve.type_examen === 'normal' ? 'S.O' : 'S.R'}</p>
                      <p>{releve.nombre_copies} copies | {releve.semestre} {releve.annee_academique}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SaisieRapideExamens;
